// Central definition of student lifecycle statuses. The persisted
// value lives on `students.status`. Historical rows may carry either
// `drop` or `dropped`; both normalise to the `dropped` bucket for
// display + filter purposes (studentsController::index already folds
// them at the SQL layer). Adding a new status is one edit here plus a
// matching backend allow-list.

export type StudentStatusKey = "active" | "dropped" | "hold";

export interface StudentStatusOption {
  /** Value UI writes back to the API + reads from `students.status`. */
  value: StudentStatusKey;
  /** Human label for badges + filter dropdown. */
  label: string;
  /** Palette anchor for the profile badge + list chip. */
  chipBgHex: string;
  chipFgHex: string;
}

export const STUDENT_STATUSES: StudentStatusOption[] = [
  { value: "active",  label: "Active",  chipBgHex: "#22c55e", chipFgHex: "#bbf7d0" },
  { value: "dropped", label: "Dropped", chipBgHex: "#ef4444", chipFgHex: "#fecaca" },
  { value: "hold",    label: "On Hold", chipBgHex: "#f59e0b", chipFgHex: "#fde68a" },
];

/**
 * Normalise a raw status string (from DB, filter param, or a legacy
 * row) to the canonical bucket. Historical rows may store `drop`
 * instead of `dropped`; treat both identically.
 */
export const normalizeStudentStatus = (raw: string | null | undefined): StudentStatusKey => {
  const s = (raw ?? "").trim().toLowerCase();
  if (s === "drop" || s === "dropped") return "dropped";
  if (s === "hold" || s === "on hold" || s === "on_hold") return "hold";
  return "active";
};

export const studentStatusLabel = (raw: string | null | undefined): string => {
  const key = normalizeStudentStatus(raw);
  return STUDENT_STATUSES.find((s) => s.value === key)?.label ?? "Active";
};

export const studentStatusChipPalette = (raw: string | null | undefined) => {
  const key = normalizeStudentStatus(raw);
  return STUDENT_STATUSES.find((s) => s.value === key) ?? STUDENT_STATUSES[0];
};

/** Convenience predicate for the `payment_due` red flag. */
export const hasPaymentDue = (row: { payment_due?: boolean; outstanding_balance?: number | null } | null | undefined): boolean =>
  !!(row && (row.payment_due === true || (row.outstanding_balance ?? 0) > 0.005));
